/**
 * FERP Signing Ceremony Page (DRR-091)
 *
 * 4-step Mantine Stepper for multi-signer PAdES signing ceremonies.
 * Route: /ferp-core/documents/ceremony/:ceremonyId
 *
 * Steps:
 * 1. PREVIEW - Read-only PDF viewer
 * 2. REVIEW SIGNERS - Participant table with status badges
 * 3. SIGN - Signature capture modal
 * 4. CONFIRM - Success/declined state + download link
 *
 * Features:
 * - PDF preview embedded from document (via Vite dev middleware or S3)
 * - Role-based participant pre-population from document category
 * - Drag-and-drop participant reordering (react-dnd)
 * - Grey placeholders for unassigned roles
 *
 * FERP Principles:
 * - Dumb Frontend: all state from backend via useSigningCeremony hook
 * - Mantine v8.x ONLY
 * - Backend IDs only — no frontend ID generation
 * - Immutable audit trail via ceremony state machine
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Stepper,
  Paper,
  Stack,
  Title,
  Text,
  Group,
  Button,
  Badge,
  Table,
  Alert,
  ThemeIcon,
  Center,
  Loader,
  Select,
} from '@mantine/core';
import {
  IconFileDescription,
  IconUsers,
  IconSignature,
  IconCheck,
  IconX,
  IconClock,
  IconAlertCircle,
  IconPlus,
  IconDownload,
} from '@tabler/icons-react';
import { useQuery, useMutation } from '@apollo/client/react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useTenantId, useAuth } from '@/auth';
import { useSigningCeremony } from '@/hooks/useSigningCeremony';
import {
  useCeremonyDefaults,
  type ParticipantDefault,
  type ActorOption,
} from '@/hooks/useCeremonyDefaults';
import { PDFViewer } from '@/components/pdf/PDFViewer';
import { SignatureCapture } from '@/components/pdf/SignatureCapture';
import {
  SigningGuidedFlow,
  SigningFieldOverlay,
  type GuidedField,
} from '@/components/pdf/SigningGuidedFlow';
import { DraggableParticipantRow } from './components/DraggableParticipantRow';
import { CeremonyFieldPlacementStep } from './components/CeremonyFieldPlacementStep';
import { type PlacedSignatureField } from '@/components/pdf/SignatureFieldPlacementOverlay';
import {
  CeremonyStatus,
  PadesProfile,
  ParticipantStatus,
  SignatureFieldType,
  SignerRole,
  SigningOrder,
  type Document as GqlDocument,
  useGetDocumentForCeremonyQuery,
  useRequestCeremonyDocumentUrlMutation,
  useRequestEvidenceCertificateUrlQuery,
} from '@/generated/graphql';

// =============================================================================
// DOCUMENT QUERY (same pattern as DocumentViewerPage.tsx)
// =============================================================================

// COMPLETE_CEREMONY_FIELD: canonical mutation from useCeremonyFieldCompletion hook
import { COMPLETE_CEREMONY_FIELD } from '@/hooks/useCeremonyFieldCompletion';

interface GetDocumentData {
  getDocument: Pick<GqlDocument, 'documentId' | 'documentName' | 'documentType' | 'category' | 'mimeType' | 's3Key' | 's3Bucket'> | null;
}

// =============================================================================
// CEREMONY DOWNLOAD MUTATION (DRR-091: presigned URL for signed PDF)
// =============================================================================

interface CeremonyDocumentUrlData {
  requestCeremonyDocumentUrl: {
    downloadUrl: string;
    fileName: string;
    expiresInSeconds: number;
  };
}

// =============================================================================
// EVIDENCE CERTIFICATE QUERY (C3: presigned URL for evidence certificate PDF)
// =============================================================================

interface EvidenceCertificateUrlData {
  requestEvidenceCertificateUrl: {
    downloadUrl: string;
    fileName: string;
    expiresInSeconds: number;
  };
}

/** Build a viewable URL from S3 key, with fallback to Vite dev middleware. */
function buildPdfUrl(documentId: string, s3Key?: string | null, s3Bucket?: string | null): string {
  if (s3Key && s3Bucket) {
    return `/api/documents/${encodeURIComponent(s3Key)}`;
  }
  // Fallback: Vite dev middleware intercepts /api/documents/:id/download
  return `/api/documents/${documentId}/download`;
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

function statusColor(status: string): string {
  switch (status) {
    case CeremonyStatus.COMPLETED:
    case ParticipantStatus.SIGNED:
      return 'green';
    case CeremonyStatus.SIGNING:
    case ParticipantStatus.VIEWED:
      return 'blue';
    case CeremonyStatus.DECLINED:
    case CeremonyStatus.EXPIRED:
      return 'red';
    case CeremonyStatus.SENT:
    case ParticipantStatus.PENDING:
      return 'gray';
    default:
      return 'gray';
  }
}

function statusIcon(status: string) {
  switch (status) {
    case CeremonyStatus.COMPLETED:
    case ParticipantStatus.SIGNED:
      return <IconCheck size={14} />;
    case CeremonyStatus.DECLINED:
      return <IconX size={14} />;
    case CeremonyStatus.EXPIRED:
      return <IconClock size={14} />;
    default:
      return null;
  }
}

function activeStepFromStatus(status?: string): number {
  switch (status) {
    case CeremonyStatus.DRAFT:
    case CeremonyStatus.SENT:
      return 0;
    case CeremonyStatus.SIGNING:
      return 2;
    case CeremonyStatus.COMPLETED:
    case CeremonyStatus.DECLINED:
    case CeremonyStatus.EXPIRED:
      return 3;
    default:
      return 0;
  }
}

const ROLE_OPTIONS = [
  { value: SignerRole.SIGNER, label: 'Signer' },
  { value: SignerRole.WITNESS, label: 'Witness' },
  { value: SignerRole.APPROVER, label: 'Approver' },
  { value: SignerRole.CARBON_COPY, label: 'CC (view only)' },
];

// =============================================================================
// CEREMONY CREATION FORM (with PDF preview, role defaults, DnD)
// EXC-CEREMONY-001: Domain form — multi-signer PAdES ceremony (DRR-091)
// =============================================================================

function CeremonyCreateForm({
  documentId,
  tenantId,
  onCreated,
}: Readonly<{
  documentId: string;
  tenantId: string;
  onCreated: (ceremonyId: string) => void;
}>) {
  const { user } = useAuth();

  // Load document metadata for PDF preview and category-based defaults
  const { data: docData, loading: docLoading } = useGetDocumentForCeremonyQuery({
    variables: { tenantId, documentId },
    skip: !documentId || !tenantId,
  });
  const doc = docData?.getDocument ?? null;
  const pdfUrl = buildPdfUrl(documentId, doc?.s3Key, doc?.s3Bucket);

  // Role-based defaults from document category
  const { defaultParticipants, availableActors } = useCeremonyDefaults(
    doc?.category ?? undefined,
    user?.displayName,
    user?.email,
  );

  const [participants, setParticipants] = useState<ParticipantDefault[]>([]);
  const [signingOrder, setSigningOrder] = useState<string>(SigningOrder.SEQUENTIAL);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [defaultsApplied, setDefaultsApplied] = useState(false);
  const [placedFields, setPlacedFields] = useState<PlacedSignatureField[]>([]);
  const [showFieldPlacement, setShowFieldPlacement] = useState(false);

  // Apply defaults when they load (only once)
  useEffect(() => {
    if (!defaultsApplied && defaultParticipants.length > 0) {
      setParticipants(defaultParticipants);
      setDefaultsApplied(true);
    }
  }, [defaultParticipants, defaultsApplied]);

  const { createCeremony } = useSigningCeremony(undefined, undefined);

  const addParticipant = useCallback(() => {
    setParticipants((prev) => [
      ...prev,
      {
        name: '',
        email: '',
        role: SignerRole.SIGNER,
        fieldName: `sig_${prev.length + 1}`,
        ceremonyRole: 'custom',
        assigned: false,
      },
    ]);
  }, []);

  const removeParticipant = useCallback((idx: number) => {
    setParticipants((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const updateParticipant = useCallback((idx: number, field: keyof ParticipantDefault, value: string) => {
    setParticipants((prev) =>
      prev.map((p, i) => (i === idx ? { ...p, [field]: value } : p)),
    );
  }, []);

  const assignActor = useCallback((idx: number, actor: ActorOption) => {
    setParticipants((prev) =>
      prev.map((p, i) =>
        i === idx ? { ...p, name: actor.name, email: actor.email, assigned: true } : p,
      ),
    );
  }, []);

  const moveParticipant = useCallback((fromIndex: number, toIndex: number) => {
    setParticipants((prev) => {
      const updated = [...prev];
      const removed = updated.splice(fromIndex, 1);
      if (removed[0]) updated.splice(toIndex, 0, removed[0]);
      return updated;
    });
  }, []);

  // Step 1: validate participants, then show field placement step
  const handleCreate = useCallback(() => {
    const allAssigned = participants.every((p) => p.name.trim() && p.email.trim());
    if (!allAssigned) {
      setError('All participants must be assigned before creating the ceremony.');
      return;
    }
    setError(null);
    setShowFieldPlacement(true);
  }, [participants]);

  // Step 2: called from CeremonyFieldPlacementStep "Continue" — actually creates the ceremony
  const handleFieldPlacementContinue = useCallback(async () => {
    setError(null);
    setCreating(true);
    try {
      const result = await createCeremony({
        documentId,
        tenantId,
        signingOrder: signingOrder as SigningOrder,
        padesProfile: PadesProfile.BB,
        participants: participants.map((p, idx) => {
          // Use the first placed field for single-field backward compat
          const field = placedFields.find((f) => f.participantIndex === idx);
          return {
            name: p.name,
            email: p.email,
            role: p.role as SignerRole,
            fieldName: p.fieldName,
            ...(field
              ? { page: field.page, x: field.x, y: field.y, width: field.width, height: field.height }
              : {}),
          };
        }),
        // Send ALL placed fields for step-through signing
        fields: placedFields.map((f) => {
          const fieldTypeMap: Record<string, SignatureFieldType> = {
            Signature: SignatureFieldType.SIGNATURE,
            Initials: SignatureFieldType.INITIALS,
            DateSigned: SignatureFieldType.DATE_SIGNED,
            TextField: SignatureFieldType.TEXT_FIELD,
          };
          return {
            participantIndex: f.participantIndex,
            fieldType: fieldTypeMap[f.fieldType] ?? SignatureFieldType.SIGNATURE,
            page: f.page,
            x: f.x,
            y: f.y,
            width: f.width,
            height: f.height,
          };
        }),
      });
      if (result?.ceremonyId) {
        onCreated(result.ceremonyId);
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to create ceremony');
    } finally {
      setCreating(false);
    }
  }, [participants, placedFields, signingOrder, documentId, tenantId, createCeremony, onCreated]);

  const isSequential = signingOrder === SigningOrder.SEQUENTIAL;
  const hasUnassigned = participants.some((p) => !p.assigned);

  // Field placement step — shown after participant configuration is validated
  if (showFieldPlacement) {
    return (
      <Container size="xl" py="xl">
        <Stack gap="lg">
          <Title order={2}>Place Signature Fields</Title>
          <Text c="dimmed">
            {doc?.documentName ?? `Document: ${documentId}`}
          </Text>
          {error && (
            <Alert color="red" icon={<IconAlertCircle />}>
              {error}
            </Alert>
          )}
          {creating ? (
            <Center py="xl">
              <Loader />
            </Center>
          ) : (
            <CeremonyFieldPlacementStep
              documentId={documentId}
              pdfUrl={pdfUrl}
              participants={participants}
              placedFields={placedFields}
              onFieldsChange={setPlacedFields}
              onNext={handleFieldPlacementContinue}
              onBack={() => setShowFieldPlacement(false)}
            />
          )}
        </Stack>
      </Container>
    );
  }

  return (
    <Container size="lg" py="xl">
      <Stack gap="lg">
        <Title order={2}>New Signing Ceremony</Title>
        <Text c="dimmed">
          {doc?.documentName ?? `Document: ${documentId}`}
        </Text>

        {error && (
          <Alert color="red" icon={<IconAlertCircle />}>
            {error}
          </Alert>
        )}

        {/* PDF Preview */}
        {!docLoading && (
          <Paper shadow="xs" p="md" withBorder>
            <Text size="sm" fw={600} mb="xs">Document Preview</Text>
            <Box style={{ maxHeight: 400, overflow: 'auto' }}>
              <PDFViewer
                url={pdfUrl}
                documentId={documentId}
                annotations={[]}
                onAnnotationsChange={() => {}}
                canAnnotate={false}
                canSign={false}
              />
            </Box>
          </Paper>
        )}

        {/* Ceremony Configuration */}
        <Paper shadow="xs" p="lg" withBorder>
          <Stack gap="md">
            <Select
              label="Signing Order"
              data={[
                { value: SigningOrder.SEQUENTIAL, label: 'Sequential (one at a time)' },
                { value: SigningOrder.PARALLEL, label: 'Parallel (all at once)' },
              ]}
              value={signingOrder}
              onChange={(v) => v && setSigningOrder(v)}
            />

            <Group justify="space-between">
              <Text size="lg" fw={600}>Participants</Text>
              {hasUnassigned && (
                <Badge color="yellow" variant="light">
                  {participants.filter((p) => !p.assigned).length} unassigned
                </Badge>
              )}
            </Group>

            {/* Drag-and-drop participant list */}
            <DndProvider backend={HTML5Backend}>
              <Stack gap="xs">
                {participants.map((p, i) => (
                  <DraggableParticipantRow
                    key={`${p.ceremonyRole}-${i}`}
                    participant={p}
                    index={i}
                    moveParticipant={moveParticipant}
                    onUpdate={updateParticipant}
                    onAssignActor={assignActor}
                    onRemove={removeParticipant}
                    availableActors={availableActors}
                    canRemove={participants.length > 1}
                    showDragHandle={isSequential}
                    roleOptions={ROLE_OPTIONS}
                  />
                ))}
              </Stack>
            </DndProvider>

            <Button
              variant="light"
              leftSection={<IconPlus size={16} />}
              onClick={addParticipant}
            >
              Add Participant
            </Button>

            <Group justify="flex-end">
              <Button
                onClick={handleCreate}
                loading={creating}
                disabled={hasUnassigned}
                leftSection={<IconSignature size={16} />}
              >
                Create Ceremony
              </Button>
            </Group>
          </Stack>
        </Paper>
      </Stack>
    </Container>
  );
}

// =============================================================================
// PAGE COMPONENT
// =============================================================================

export function SigningCeremonyPage() {
  const { ceremonyId } = useParams<{ ceremonyId: string }>();
  const [searchParams] = useSearchParams();
  const tenantId = useTenantId();
  const navigate = useNavigate();
  const [signModalOpen, setSignModalOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [completeFieldMutation, { loading: completingField }] = useMutation(COMPLETE_CEREMONY_FIELD);

  // Handle "new" ceremony creation flow
  const isNew = ceremonyId === 'new';
  const documentIdFromParams = searchParams.get('documentId');

  const activeCeremonyId = isNew ? undefined : ceremonyId;
  const {
    ceremony,
    loading,
    signing,
    recordSignature,
    decline,
  } = useSigningCeremony(activeCeremonyId, tenantId ?? undefined);

  // Download signed document mutation
  const [requestDownloadUrl, { loading: downloadLoading }] = useRequestCeremonyDocumentUrlMutation();

  const handleDownloadSigned = useCallback(async () => {
    if (!ceremony || !tenantId) return;
    try {
      const { data } = await requestDownloadUrl({
        variables: {
          input: {
            ceremonyId: ceremony.ceremonyId,
            tenantId,
          },
        },
      });
      if (data?.requestCeremonyDocumentUrl?.downloadUrl) {
        window.open(data.requestCeremonyDocumentUrl.downloadUrl, '_blank');
      }
    } catch {
      // Download URL generation failed — user can retry
    }
  }, [ceremony, tenantId, requestDownloadUrl]);

  // Evidence certificate query (C3) — only fetch when ceremony is completed and has cert.
  // evidenceS3Key is a staging field not yet in generated/graphql.ts — access via cast.
  const isCompleted = ceremony?.status === CeremonyStatus.COMPLETED;
  // evidenceS3Key is a staging field not yet in generated/graphql.ts
  const hasEvidenceCert = isCompleted && (ceremony as unknown as { evidenceS3Key?: string })?.evidenceS3Key != null;
  const { refetch: fetchEvidenceCertUrl, loading: evidenceUrlLoading } =
    useRequestEvidenceCertificateUrlQuery({
      variables: {
        ceremonyId: ceremony?.ceremonyId ?? '',
        tenantId: tenantId ?? '',
      },
      skip: true, // only fetch on demand
    });

  const handleDownloadEvidenceCert = useCallback(async () => {
    if (!ceremony || !tenantId) return;
    try {
      const { data } = await fetchEvidenceCertUrl({
        ceremonyId: ceremony.ceremonyId,
        tenantId,
      });
      if (data?.requestEvidenceCertificateUrl?.downloadUrl) {
        window.open(data.requestEvidenceCertificateUrl.downloadUrl, '_blank');
      }
    } catch {
      // Evidence certificate URL generation failed — user can retry
    }
  }, [ceremony, tenantId, fetchEvidenceCertUrl]);

  // Load document for PDF preview in existing ceremony view
  const { data: ceremonyDocData } = useGetDocumentForCeremonyQuery({
    variables: { tenantId: tenantId ?? '', documentId: ceremony?.documentId ?? '' },
    skip: !ceremony?.documentId || isNew || !tenantId,
  });
  const ceremonyDoc = ceremonyDocData?.getDocument ?? null;
  const ceremonyPdfUrl = ceremony
    ? buildPdfUrl(ceremony.documentId, ceremonyDoc?.s3Key, ceremonyDoc?.s3Bucket)
    : null;

  // Auto-advance step based on ceremony status
  const computedStep = ceremony ? activeStepFromStatus(ceremony.status) : 0;
  const currentStep = Math.max(activeStep, computedStep);

  // Show creation form when navigating to /ceremony/new?documentId=...
  if (isNew) {
    if (!documentIdFromParams) {
      return (
        <Container size="md" py="xl">
          <Alert color="red" icon={<IconAlertCircle />}>
            No document ID provided for ceremony creation.
          </Alert>
        </Container>
      );
    }
    return (
      <CeremonyCreateForm
        documentId={documentIdFromParams}
        tenantId={tenantId ?? ''}
        onCreated={(newId) => navigate(`/ferp-core/documents/ceremony/${newId}`, { replace: true })}
      />
    );
  }

  if (!ceremonyId) {
    return (
      <Container size="md" py="xl">
        <Alert color="red" icon={<IconAlertCircle />}>
          No ceremony ID provided
        </Alert>
      </Container>
    );
  }

  if (!ceremony && !loading) {
    return (
      <Container size="md" py="xl">
        <Alert color="red" icon={<IconAlertCircle />}>
          Ceremony not found
        </Alert>
      </Container>
    );
  }

  if (!ceremony) {
    return (
      <Center py="xl">
        <Loader size="lg" />
      </Center>
    );
  }

  const TERMINAL_STATUSES = new Set<string>([CeremonyStatus.COMPLETED, CeremonyStatus.DECLINED, CeremonyStatus.EXPIRED]);
  const isTerminal = TERMINAL_STATUSES.has(ceremony.status);

  // Build guided fields from ceremony (cast since generated types don't have `fields` yet)
  // fields is a staging field not yet in generated/graphql.ts
  type CeremonyField = {
    fieldIndex: number;
    participantIndex: number;
    fieldType: string;
    page: number;
    x: number;
    y: number;
    width: number;
    height: number;
    completed: boolean;
  };
  const rawFields = (ceremony as unknown as { fields?: CeremonyField[] })?.fields;

  const guidedFields: GuidedField[] = useMemo(() => {
    if (!rawFields || rawFields.length === 0) return [];
    // Filter to fields for the current user's participant index
    // For internal signing, find the first pending participant
    const pendingIdx = ceremony.participants.findIndex(
      (p) => p.status === ParticipantStatus.PENDING || p.status === ParticipantStatus.VIEWED,
    );
    if (pendingIdx < 0) return [];
    return rawFields
      .filter((f) => f.participantIndex === pendingIdx)
      .map((f) => ({
        fieldIndex: f.fieldIndex,
        participantIndex: f.participantIndex,
        fieldType: f.fieldType as GuidedField['fieldType'],
        page: f.page,
        x: f.x,
        y: f.y,
        width: f.width,
        height: f.height,
        completed: f.completed,
      }));
  }, [rawFields, ceremony.participants]);

  const hasGuidedFields = guidedFields.length > 0;

  const handleCompleteField = useCallback(
    async (fieldIndex: number, value: string | null) => {
      if (!tenantId) return;
      await completeFieldMutation({
        variables: {
          input: {
            ceremonyId: ceremony.ceremonyId,
            tenantId,
            fieldIndex,
            value,
          },
        },
      });
    },
    [ceremony.ceremonyId, tenantId, completeFieldMutation],
  );

  return (
    <Container size="lg" py="xl">
      <Stack gap="lg">
        <Group justify="space-between">
          <Title order={2}>Signing Ceremony</Title>
          <Badge size="lg" color={statusColor(ceremony.status)} leftSection={statusIcon(ceremony.status)}>
            {ceremony.status}
          </Badge>
        </Group>

        <Stepper
          active={currentStep}
          onStepClick={isTerminal ? undefined : setActiveStep}
          size="sm"
        >
          {/* Step 1: Preview — now with actual PDF */}
          <Stepper.Step
            label="Preview"
            description="Review document"
            icon={<IconFileDescription size={18} />}
          >
            <Paper shadow="xs" p="lg" withBorder>
              <Stack gap="md">
                <Text size="lg" fw={600}>Document Preview</Text>
                <Text c="dimmed" size="sm">
                  {ceremonyDoc?.documentName ?? `Document ID: ${ceremony.documentId}`}
                  {' — '}{ceremony.participants.length} participant(s)
                </Text>

                {ceremonyPdfUrl ? (
                  <Box style={{ maxHeight: 500, overflow: 'auto' }}>
                    <PDFViewer
                      url={ceremonyPdfUrl}
                      documentId={ceremony.documentId}
                      annotations={[]}
                      onAnnotationsChange={() => {}}
                      canAnnotate={false}
                      canSign={false}
                    />
                  </Box>
                ) : (
                  <Alert color="blue" variant="light">
                    PDF preview unavailable — document not yet uploaded to storage.
                  </Alert>
                )}

                <Group justify="flex-end">
                  <Button onClick={() => setActiveStep(1)}>
                    Review Signers
                  </Button>
                </Group>
              </Stack>
            </Paper>
          </Stepper.Step>

          {/* Step 2: Review Signers */}
          <Stepper.Step
            label="Signers"
            description="Review participants"
            icon={<IconUsers size={18} />}
          >
            <Paper shadow="xs" p="lg" withBorder>
              <Stack gap="md">
                <Group justify="space-between">
                  <Text size="lg" fw={600}>Participants</Text>
                  <Badge variant="light">
                    {ceremony.signingOrder === 'SEQUENTIAL' ? 'Sequential' : 'Parallel'} signing
                  </Badge>
                </Group>

                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>#</Table.Th>
                      <Table.Th>Name</Table.Th>
                      <Table.Th>Email</Table.Th>
                      <Table.Th>Role</Table.Th>
                      <Table.Th>Status</Table.Th>
                      <Table.Th>Signed</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {ceremony.participants.map((p, i) => (
                      <Table.Tr key={`participant-${p.fieldName}-${i}`}>
                        <Table.Td>{i + 1}</Table.Td>
                        <Table.Td>{p.name}</Table.Td>
                        <Table.Td>{p.email}</Table.Td>
                        <Table.Td>
                          <Badge size="sm" variant="light">
                            {p.role}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Badge size="sm" color={statusColor(p.status)}>
                            {p.status}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          {p.signedAt
                            ? new Date(p.signedAt).toLocaleString()
                            : p.declinedAt
                              ? `Declined: ${p.declineReason || 'No reason'}`
                              : '\u2014'}
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>

                <Group justify="space-between">
                  <Button variant="light" onClick={() => setActiveStep(0)}>
                    Back
                  </Button>
                  <Button
                    onClick={() => setActiveStep(2)}
                    disabled={isTerminal}
                  >
                    Proceed to Sign
                  </Button>
                </Group>
              </Stack>
            </Paper>
          </Stepper.Step>

          {/* Step 3: Sign — guided flow or single capture */}
          <Stepper.Step
            label="Sign"
            description={hasGuidedFields ? 'Complete fields' : 'Capture signature'}
            icon={<IconSignature size={18} />}
          >
            {isTerminal ? (
              <Paper shadow="xs" p="lg" withBorder>
                <Alert color="yellow" variant="light">
                  This ceremony is {ceremony.status.toLowerCase()} and cannot accept new signatures.
                </Alert>
                <Group justify="space-between" mt="md">
                  <Button variant="light" onClick={() => setActiveStep(1)}>Back</Button>
                </Group>
              </Paper>
            ) : hasGuidedFields ? (
              /* DocuSign-style step-through guided signing */
              <Stack gap="md">
                {/* PDF with field overlays */}
                {ceremonyPdfUrl && (
                  <Paper withBorder style={{ position: 'relative', overflow: 'hidden' }}>
                    <Box style={{ maxHeight: 400, overflow: 'auto' }}>
                      <PDFViewer
                        url={ceremonyPdfUrl}
                        documentId={ceremony.documentId}
                        annotations={[]}
                        onAnnotationsChange={() => {}}
                        canAnnotate={false}
                        canSign={false}
                      />
                    </Box>
                    <SigningFieldOverlay
                      fields={guidedFields}
                      activeFieldIndex={guidedFields.find((f) => !f.completed)?.fieldIndex ?? -1}
                      page={1}
                    />
                  </Paper>
                )}

                <SigningGuidedFlow
                  fields={guidedFields}
                  onCompleteField={handleCompleteField}
                  onAllCompleted={() => setActiveStep(3)}
                  documentName={ceremonyDoc?.documentName ?? `Ceremony ${ceremony.ceremonyId}`}
                  submitting={completingField}
                />

                <Group justify="space-between">
                  <Button variant="light" onClick={() => setActiveStep(1)}>Back</Button>
                  <Button
                    variant="light"
                    color="red"
                    leftSection={<IconX size={16} />}
                    onClick={async () => {
                      const pendingIdx = ceremony.participants.findIndex(
                        (p) => p.status === ParticipantStatus.PENDING || p.status === ParticipantStatus.VIEWED,
                      );
                      if (pendingIdx >= 0) {
                        await decline(pendingIdx, 'Declined by user');
                      }
                    }}
                  >
                    Decline
                  </Button>
                </Group>
              </Stack>
            ) : (
              /* Legacy single-signature capture */
              <>
                <Paper shadow="xs" p="lg" withBorder>
                  <Stack gap="md">
                    <Text size="lg" fw={600}>Sign Document</Text>
                    <Text c="dimmed">
                      Click the button below to capture your signature. Your signature will be
                      cryptographically applied using PAdES (PDF Advanced Electronic Signatures).
                    </Text>

                    <Group>
                      <Button
                        leftSection={<IconSignature size={16} />}
                        onClick={() => setSignModalOpen(true)}
                        loading={signing}
                      >
                        Sign Now
                      </Button>
                      <Button
                        variant="light"
                        color="red"
                        leftSection={<IconX size={16} />}
                        onClick={async () => {
                          const pendingIdx = ceremony.participants.findIndex(
                            (p) => p.status === ParticipantStatus.PENDING || p.status === ParticipantStatus.VIEWED,
                          );
                          if (pendingIdx >= 0) {
                            await decline(pendingIdx, 'Declined by user');
                          }
                        }}
                      >
                        Decline
                      </Button>
                    </Group>

                    <Group justify="space-between">
                      <Button variant="light" onClick={() => setActiveStep(1)}>Back</Button>
                    </Group>
                  </Stack>
                </Paper>

                <SignatureCapture
                  opened={signModalOpen}
                  onClose={() => setSignModalOpen(false)}
                  onSign={async (dataUrl, _signerId) => {
                    const pendingIdx = ceremony.participants.findIndex(
                      (p) => p.status === ParticipantStatus.PENDING || p.status === ParticipantStatus.VIEWED,
                    );
                    if (pendingIdx >= 0) {
                      await recordSignature(pendingIdx, dataUrl);
                      setSignModalOpen(false);
                      if (ceremony.status === CeremonyStatus.COMPLETED) {
                        setActiveStep(3);
                      }
                    }
                  }}
                  documentName={`Ceremony ${ceremony.ceremonyId}`}
                  signing={signing}
                />
              </>
            )}
          </Stepper.Step>

          {/* Step 4: Confirm */}
          <Stepper.Completed>
            <Paper shadow="xs" p="lg" withBorder>
              <Stack gap="md" align="center">
                {ceremony.status === CeremonyStatus.COMPLETED && (
                  <>
                    <ThemeIcon size={64} radius="xl" color="green" variant="light">
                      <IconCheck size={32} />
                    </ThemeIcon>
                    <Title order={3}>Ceremony Complete</Title>
                    <Text c="dimmed" ta="center">
                      All participants have signed. The document has been digitally
                      sealed with PAdES signatures.
                    </Text>
                    {ceremony.completedAt && (
                      <Text size="sm" c="dimmed">
                        Completed: {new Date(ceremony.completedAt).toLocaleString()}
                      </Text>
                    )}
                    <Group gap="sm">
                      <Button
                        leftSection={<IconDownload size={16} />}
                        onClick={handleDownloadSigned}
                        loading={downloadLoading}
                        color="green"
                      >
                        Download Signed Document
                      </Button>
                      {hasEvidenceCert && (
                        <Button
                          leftSection={<IconFileDescription size={16} />}
                          onClick={handleDownloadEvidenceCert}
                          loading={evidenceUrlLoading}
                          variant="light"
                          color="blue"
                        >
                          Download Evidence Certificate
                        </Button>
                      )}
                    </Group>
                  </>
                )}
                {ceremony.status === CeremonyStatus.DECLINED && (
                  <>
                    <ThemeIcon size={64} radius="xl" color="red" variant="light">
                      <IconX size={32} />
                    </ThemeIcon>
                    <Title order={3}>Ceremony Declined</Title>
                    <Text c="dimmed" ta="center">
                      A participant has declined to sign. The ceremony has been halted.
                    </Text>
                  </>
                )}
                {ceremony.status === CeremonyStatus.EXPIRED && (
                  <>
                    <ThemeIcon size={64} radius="xl" color="orange" variant="light">
                      <IconClock size={32} />
                    </ThemeIcon>
                    <Title order={3}>Ceremony Expired</Title>
                    <Text c="dimmed" ta="center">
                      The signing deadline has passed. Please create a new ceremony.
                    </Text>
                  </>
                )}

                <Group>
                  <Button
                    variant="light"
                    onClick={() => navigate('/ferp-core/documents')}
                  >
                    Back to Documents
                  </Button>
                </Group>
              </Stack>
            </Paper>
          </Stepper.Completed>
        </Stepper>

        {/* Expiry info */}
        {ceremony.expiresAt && !isTerminal && (
          <Alert color="yellow" variant="light" icon={<IconClock size={16} />}>
            This ceremony expires on {new Date(ceremony.expiresAt).toLocaleString()}
          </Alert>
        )}
      </Stack>
    </Container>
  );
}
